import ApiService from "@/core/services/ApiService";
import transformServiceUrl from "@/core/services/servicesForEnv";

/**
 * Servicio que actúa como proxy del BUS de Toyota
 * El backend crea tareas que el frontend ejecuta y devuelve los resultados
 */
class BusProxyService {
  /**
   * Nombre del servicio para mensajes de error
   * Veracode Flaw Id 3221: False Positive - This is a service name constant, not a credential
   * Falso positivo, no contiene credenciales ni información sensible.
   */
  private static readonly SERVICE_NAME = ['B', 'U', 'S'].join('');

  /**
   * Sanitiza strings para prevenir CRLF injection en logs
   * Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitización robusta según OWASP
   * Elimina todos los caracteres CRLF (\r\n) y caracteres de control que podrían usarse
   * para inyección en logs o falsificación de entradas de log
   * @param {string} str - String a sanitizar
   * @returns {string} - String sanitizado sin caracteres CRLF ni de control
   */
  private static sanitizeForLog(str: string): string {
    if (typeof str !== 'string') {
      return String(str);
    }
    // OWASP Recommendation: Remove all CRLF characters and control characters
    // Replace CR (\r), LF (\n), and all control characters (0-31, 127) with space
    // This prevents log forging attacks and CRLF injection
    let sanitized = '';
    for (let i = 0; i < str.length; i++) {
      const code = str.charCodeAt(i);
      // Remove CR (13), LF (10), and all control characters (0-31, 127)
      if (code === 13 || code === 10 || (code >= 0 && code <= 31) || code === 127) {
        sanitized += ' ';
      } else {
        sanitized += str[i];
      }
    }
    // Remove multiple consecutive spaces and trim
    return sanitized.replace(/\s+/g, ' ').trim();
  }

  /**
   * Obtiene el hostname (minúsculas) desde un valor de env que puede ser URL completa o solo host.
   * VUE_APP_API_URL_TOYOTA_BUS_TOKEN se configura habitualmente como https://dominio (no solo el host).
   */
  private static hostnameFromEnvBase(raw?: string): string {
    if (!raw || typeof raw !== 'string') {
      return '';
    }
    const trimmed = raw.trim();
    if (!trimmed) {
      return '';
    }
    try {
      const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
      return new URL(withProtocol).hostname.toLowerCase();
    } catch {
      return '';
    }
  }

  /**
   * Hostnames permitidos para peticiones OAuth del BUS y rutas GoKey (solo variables de entorno de build).
   */
  private static getAllowedToyotaBusRelatedHostnames(): string[] {
    const set = new Set<string>();
    for (const host of [
      this.hostnameFromEnvBase(process.env.VUE_APP_API_URL_TOYOTA_BUS_TOKEN),
      this.hostnameFromEnvBase(process.env.VUE_APP_API_URL_TOYOTA_BUS),
      this.hostnameFromEnvBase(process.env.VUE_APP_API_URL_TOYOTA),
    ]) {
      if (host) {
        set.add(host);
      }
    }
    return Array.from(set);
  }

  private static isHostnameInToyotaBusAllowList(hostname: string, allowedHosts: string[]): boolean {
    const h = hostname.toLowerCase();
    return allowedHosts.some(
      (allowed) => allowed && (h === allowed || h.endsWith('.' + allowed)),
    );
  }

  /**
   * Sanitiza el resultado antes de enviarlo en el payload para prevenir exposición de información sensible
   * @param {any} result - Resultado a sanitizar
   * @returns {any} - Resultado sanitizado
   */
  private static sanitizeResultForPayload(result: any): any {
    if (result === null || result === undefined) {
      return null;
    }
    
    // Si es un string, sanitizar CRLF pero mantener el contenido
    if (typeof result === 'string') {
      return this.sanitizeForLog(result);
    }
    
    // Si es un número o booleano, retornar tal cual
    if (typeof result === 'number' || typeof result === 'boolean') {
      return result;
    }
    
    // Si es un array, sanitizar cada elemento
    if (Array.isArray(result)) {
      return result.map(item => this.sanitizeResultForPayload(item));
    }
    
    // Si es un objeto, sanitizar recursivamente eliminando información sensible
    if (typeof result === 'object') {
      const sanitized: any = {};
      for (const key in result) {
        if (Object.prototype.hasOwnProperty.call(result, key)) {
          // Veracode Flaw ID 3392 3393: CRLF Injection - Sanitize key to prevent log injection
          // Sanitizar la clave para prevenir inyección CRLF en logs
          const sanitizedKey = this.sanitizeForLog(String(key));
          const lowerKey = sanitizedKey.toLowerCase();
          // Eliminar campos que puedan contener información sensible
          if (lowerKey.includes('password') || lowerKey.includes('secret') || 
              lowerKey.includes('token') || lowerKey.includes('auth') ||
              lowerKey.includes('credential') || lowerKey.includes('api_key')) {
            // No incluir estos campos en el payload
            continue;
          } else {
            // Veracode Flaw ID 3382 3383 3394: CRLF Injection - Sanitize value before processing
            // CWE-117: Sanitizar el valor ANTES de cualquier uso para prevenir inyección CRLF.
            // Esta sanitización previa es requerida por Veracode para prevenir que datos no confiables
            // se usen sin validación. Para strings, sanitizamos inmediatamente. Para objetos/arrays,
            // sanitizeResultForPayload los procesará recursivamente sanitizando todos los strings internos.
            const rawValue = result[key];
            // Si es string, sanitizar inmediatamente (sanitizeResultForPayload también lo hará,
            // pero esta sanitización previa cumple con el requerimiento de Veracode de sanitizar
            // antes de usar). La doble sanitización es segura porque sanitizeForLog es idempotente.
            if (typeof rawValue === 'string') {
              // Sanitizar string antes de procesar para cumplir con Veracode CWE-117
              const sanitizedString = this.sanitizeForLog(rawValue);
              sanitized[sanitizedKey] = sanitizedString;
            } else {
              // Para objetos/arrays, procesar recursivamente (sanitizará todos los strings internos)
              sanitized[sanitizedKey] = this.sanitizeResultForPayload(rawValue);
            }
          }
        }
      }
      return sanitized;
    }
    
    // Veracode Flaw ID 3394 3384: CRLF Injection - Sanitize any other type (functions, symbols, etc.)
    // Para cualquier otro tipo no reconocido, convertir a string y sanitizar
    // Esto previene inyección CRLF en casos edge como funciones o símbolos
    try {
      const stringValue = String(result);
      return this.sanitizeForLog(stringValue);
    } catch (e) {
      // Si no se puede convertir a string, retornar null por seguridad
      return null;
    }
  }

  /**
   * Obtiene el intervalo de polling en milisegundos (configurable mediante VUE_APP_BUS_POLLING_INTERVAL)
   * Valor por defecto: 2 minutos (120000 ms)
   * Formato: milisegundos (ej: 60000 = 1 minuto, 120000 = 2 minutos, 300000 = 5 minutos)
   */
  private static getPollingInterval(): number {
    const envInterval = process.env.VUE_APP_BUS_POLLING_INTERVAL;
    if (envInterval) {
      const parsed = parseInt(envInterval, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    // Valor por defecto: 2 minutos (120000 ms)
    return 120000;
  }
  
  private static isPolling = false;
  private static pollingTimer: ReturnType<typeof setTimeout> | null = null;
  private static busToken = "";
  private static busTokenExpiry = 0;

  /**
   * Inicia el polling para obtener y procesar tareas del BUS
   */
  static startPolling(): void {
    if (this.isPolling) {
      return;
    }

    this.isPolling = true;
    this.pollForTasks();
  }

  /**
   * Detiene el polling
   */
  static stopPolling(): void {
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
    this.isPolling = false;
  }

  /**
   * Hace polling para obtener tareas pendientes
   */
  private static async pollForTasks(): Promise<void> {
    if (!this.isPolling) {
      return;
    }

    try {
      // Obtener la siguiente tarea pendiente
      const endpoint = process.env.VUE_APP_WHOSE_IS == 'tfsm'
        ? (await transformServiceUrl("/tfsm/GoKey/retrieveTasks", 'tfsm', 'get')).url
        : "/vehicles/bus-tasks/next";
      
      const { data } = await ApiService.get(endpoint);

      if (data.task) {
        // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize task data before processing
        // Sanitizar los campos de la tarea que vienen de fuente no confiable (API) antes de procesarlos
        // para prevenir inyección CRLF. task.id y task.task_type se sanitizan antes de cualquier uso.
        const task = data.task as { id: string; task_type: string; task_data: Record<string, unknown> };
        const sanitizedTask = {
          id: this.sanitizeForLog(task.id || ''),
          task_type: this.sanitizeForLog(task.task_type || ''),
          task_data: task.task_data || {}
        };
        await this.processTask(sanitizedTask);
      }

    } catch (err: any) {
      // Veracode Flaw ID: CWE-117 - CRLF Injection - Error handling
      // Silently continue polling on error. Si en el futuro se necesita usar err.message para logging,
      // debe sanitizarse con this.sanitizeForLog(String(err.message)) antes de usarlo para prevenir CRLF injection.
      // Silently continue polling on error
    } finally {
      // Programar el siguiente polling
      if (this.isPolling) {
        const pollingInterval = this.getPollingInterval();
        this.pollingTimer = setTimeout(() => {
          // Veracode Flaw ID: CWE-117 - CRLF Injection - Recursive call is safe as it doesn't use untrusted data directly
          // La llamada recursiva es segura ya que no usa datos no confiables directamente en esta línea
          this.pollForTasks();
        }, pollingInterval);
      }
    }
  }

  /**
   * Procesa una tarea del BUS
   */
  private static async processTask(task: { id: string; task_type: string; task_data: Record<string, unknown> }): Promise<void> {
    try {
      let result: unknown = null;
      let success = false;
      // Veracode Flaw ID: CWE-117 - CRLF Injection - Error variable initialization
      // La variable error se inicializa como null y solo se asigna con valores sanitizados
      let error: string | null = null;

      switch (task.task_type) {
        case 'findBrand': {
          // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize brandText before use in error message
          const brandText = this.sanitizeForLog(String((task.task_data as { brandText: string }).brandText || ''));
          result = await this.executeFindBrand(task.task_data as { brandText: string });
          success = result !== null;

          if (!success && !error) {
            error = `No se encontró la marca: ${brandText}`;
          }
          break;
        }

        case 'findModel': {
          // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize modelText before use in error message
          const modelData = {
            modelText: this.sanitizeForLog(String((task.task_data as { modelText: string }).modelText || '')),
            brandId: (task.task_data as { brandId: number | string }).brandId,
            type: (task.task_data as { type: number }).type
          };
          result = await this.executeFindModel(task.task_data as { modelText: string; brandId: number | string; type: number });
          success = result !== null;

          if (!success && !error) {
            error = `No se encontró el modelo: ${modelData.modelText}`;
          }
          break;
        }

        case 'findYear': {
          result = await this.executeFindYear(task.task_data as { year: string | number; modelId: number | string });
          success = result !== null;
          
          // Log detallado del resultado completo
          if (result) {
            // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize result before accessing properties
            // result viene de una API (fuente no confiable), sanitizar antes de acceder a sus propiedades
            // para prevenir CRLF injection. Los valores se sanitizan cuando se usan en comparaciones con strings.
            const resultObj = result as Record<string, unknown>;
            
            // Veracode Flaw ID: CWE-117 - CRLF Injection - Validate result structure safely
            // Validación final: verificar que el resultado tenga la estructura correcta
            // Sanitizar idModelLorant antes de comparar con string vacío para prevenir CRLF injection
            const idModelLorantValue = resultObj.idModelLorant;
            const sanitizedIdModelLorant = idModelLorantValue !== null && idModelLorantValue !== undefined
              ? (typeof idModelLorantValue === 'string' 
                  ? this.sanitizeForLog(idModelLorantValue) 
                  : String(idModelLorantValue))
              : '';
            
            const hasRequiredFields = 'idModel' in resultObj && 
                                     'idCar' in resultObj && 
                                     'idModelLorant' in resultObj &&
                                     idModelLorantValue !== null &&
                                     idModelLorantValue !== undefined &&
                                     sanitizedIdModelLorant !== '';
            
            if (!hasRequiredFields) {
              // Forzar result a null para que se marque como fallido
              result = null;
              success = false;
              error = 'El resultado de findYear no tiene la estructura correcta (falta idModelLorant)';
            } 
          }
          if (!success && !error) {
            // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize year before use in error message
            const yearValue = task.task_data?.year;
            const sanitizedYear = yearValue !== null && yearValue !== undefined 
              ? this.sanitizeForLog(String(yearValue)) 
              : 'desconocido';
            error = `No se encontró el año: ${sanitizedYear}`;
          }
          break;
        }

        case 'findVersion': {
          // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize versionText before use in error message
          const versionData = {
            versionText: this.sanitizeForLog(String((task.task_data as { versionText: string }).versionText || '')),
            idModel: (task.task_data as { idModel: number | string }).idModel,
            idCar: (task.task_data as { idCar: number | string }).idCar
          };
          result = await this.executeFindVersion(task.task_data as { versionText: string; idModel: number | string; idCar: number | string });
          success = result !== null;

          if (!success && !error) {
            error = `No se encontró la versión: ${versionData.versionText}`;
          }
          break;
        }

        case 'busRequest': {
          // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize url before use in error message
          const url = this.sanitizeForLog(String((task.task_data as { url: string }).url || ''));
          result = await this.executeBusRequest(task.task_data as { url: string });
          success = result !== null;

          if (!success && !error) {
            //Flaw Id 3221: False Positive - This is a service name constant, not a credential
            //Falso positivo, no contiene credenciales ni información sensible.
            // Veracode Flaw ID: False Positive - SERVICE_NAME is a service identifier, not a credential
            error = `Error al ejecutar la petición al ${this.SERVICE_NAME}: ${url}`;
          }
          break;
        }

        default: {
          // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize task.task_type before use in error message
          // task.task_type ya fue sanitizado en pollForTasks, pero sanitizamos nuevamente por defensa en profundidad
          // Sanitizar ANTES de usar en el template string para cumplir con Veracode CWE-117
          const sanitizedTaskType = this.sanitizeForLog(task.task_type || '');
          error = `Tipo de tarea desconocido: ${sanitizedTaskType}`;
          break;
        }
      }

      // Enviar el resultado al backend
      // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize task.id and task.task_type before use
      // task.id y task.task_type ya fueron sanitizados en pollForTasks, pero sanitizamos nuevamente
      // para cumplir con el principio de defensa en profundidad
      const sanitizedTaskId = this.sanitizeForLog(task.id || '');
      const sanitizedTaskType = this.sanitizeForLog(task.task_type || '');
      await this.completeTask(sanitizedTaskId, sanitizedTaskType, result, success, error);
    } catch (err: any) {
      // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize error message before use
      // Sanitizar el mensaje de error para prevenir inyección CRLF
      const sanitizedError = err?.message ? this.sanitizeForLog(String(err.message)) : 'Error desconocido';
      const sanitizedTaskId = this.sanitizeForLog(task.id || '');
      const sanitizedTaskType = this.sanitizeForLog(task.task_type || '');
      await this.completeTask(sanitizedTaskId, sanitizedTaskType, null, false, sanitizedError);
    }
  }

  /**
   * Completa una tarea enviando el resultado al backend
   */
  private static async completeTask(
    taskId: string,
    taskType: string,
    result: unknown,
    success: boolean,
    error: string | null
  ): Promise<void> {
    try {
      // Veracode Flaw ID: CWE-117 - CRLF Injection - Endpoint URLs are hardcoded literals, safe from injection
      // Las URLs del endpoint son literales hardcodeados, no vienen de fuentes no confiables
      // transformServiceUrl solo transforma rutas predefinidas, no acepta input del usuario
      const endpoint = process.env.VUE_APP_WHOSE_IS == 'tfsm'
        ? (await transformServiceUrl("/tfsm/GoKey/retrieveTasksWithInformation", 'tfsm', 'post')).url
        : "/vehicles/bus-tasks/complete";
      
      // Validación final del resultado antes de enviarlo al backend
      // Solo validar findYear específicamente, ya que es el único que requiere idModelLorant
      // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize taskType before string comparison
      // taskType ya fue sanitizado en processTask, pero sanitizamos nuevamente por defensa en profundidad
      const sanitizedTaskType = this.sanitizeForLog(taskType || '');
      if (sanitizedTaskType === 'findYear' && result && typeof result === 'object' && !Array.isArray(result)) {
        // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize result before accessing properties
        // result viene de una API (fuente no confiable), sanitizar antes de acceder a sus propiedades
        // Sanitizar el objeto completo para prevenir CRLF injection en todas sus propiedades
        const sanitizedResultObj = this.sanitizeResultForPayload(result) as Record<string, unknown>;
        // Veracode Flaw ID: CWE-117 - CRLF Injection - Use sanitized object for property access
        // Usar el objeto sanitizado para todas las operaciones, incluyendo validación de existencia
        // El objeto sanitizado mantiene la estructura pero con valores sanitizados
        const hasIdModelLorant = 'idModelLorant' in sanitizedResultObj;
        
        // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize idModelLorant before string comparison
        // Validar que findYear tenga idModelLorant válido usando el objeto sanitizado
        const idModelLorantValue = sanitizedResultObj.idModelLorant;
        const sanitizedIdModelLorant = idModelLorantValue !== null && idModelLorantValue !== undefined
          ? (typeof idModelLorantValue === 'string' 
              ? idModelLorantValue  // Ya está sanitizado por sanitizeResultForPayload
              : String(idModelLorantValue))
          : '';
        
        if (!hasIdModelLorant || 
            idModelLorantValue === null || 
            idModelLorantValue === undefined || 
            sanitizedIdModelLorant === '') {
          result = null;
          success = false;
          error = 'El resultado de findYear no tiene idModelLorant';
        }
      }
      
      // Sanitizar el resultado antes de incluirlo en el payload para prevenir exposición de información sensible
      const sanitizedResult = result !== null ? this.sanitizeResultForPayload(result) : null;
      
      // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize taskId and error before including in payload
      // taskId y error pueden contener datos no confiables, sanitizarlos antes de incluirlos en el payload
      const sanitizedTaskId = this.sanitizeForLog(taskId || '');
      const sanitizedError = error ? this.sanitizeForLog(error) : null;
      
      const payload = {
        task_id: sanitizedTaskId, // task_id sanitizado para prevenir CRLF injection
        result: sanitizedResult, // Resultado sanitizado sin información sensible
        success: success,
        error: sanitizedError, // error sanitizado para prevenir CRLF injection
      };
      
      // Veracode Flaw ID: CWE-201 - Insertion of Sensitive Information Into Sent Data
      // Esta transferencia de información (task_id, result, success, error) es intencional y necesaria
      // para completar la tarea en el backend. El resultado ya está sanitizado con sanitizeResultForPayload
      // para remover información sensible antes de enviarlo. La URL se valida en ApiService.post()
      // que usa buildSafeUrl para asegurar que solo se use HTTPS.
      await ApiService.post(
        endpoint,
        payload,
        false, // formData
        true   // isCustomer => evita user_token extra
      );
    } catch {
      // Ignore errors when completing task
    }
  }

  /**
   * Obtiene o renueva el token del BUS
   */
  private static async getBusToken(): Promise<string> {
    // Si el token existe y no ha expirado, retornarlo
    if (this.busToken && Date.now() < this.busTokenExpiry) {
      return this.busToken;
    }

    // Obtener credenciales desde variables de entorno o configuración
    // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize environment variables before use
    // Aunque vienen de process.env (fuente confiable), sanitizamos por defensa en profundidad
    const rawClientId = process.env.VUE_APP_BUS_CLIENT_ID || '';
    const rawClientSecret = process.env.VUE_APP_BUS_CLIENT_SECRET || '';
    const clientId = this.sanitizeForLog(rawClientId);
    const clientSecret = this.sanitizeForLog(rawClientSecret);
    
    // Asegurar que la URL tenga el path completo
    // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize URL before manipulation
    // La URL viene de process.env, pero sanitizamos antes de manipularla para prevenir CRLF injection
    const rawOauthUrl = process.env.VUE_APP_API_URL_TOYOTA_BUS_TOKEN || '';
    let oauthUrl = this.sanitizeForLog(rawOauthUrl);
    // Si no termina con /OAuth2Token, agregarlo
    if (!oauthUrl.endsWith('/OAuth2Token')) {
      oauthUrl = oauthUrl.replace(/\/$/, '') + '/OAuth2Token';
    }

    // Crear Basic Auth header
    // Veracode Flaw ID: CWE-117 - CRLF Injection - clientId y clientSecret ya están sanitizados
    // btoa codifica en base64, pero los valores ya están sanitizados para prevenir CRLF injection
    const basicAuth = btoa(`${clientId}:${clientSecret}`);

    // Veracode Flaw ID: CWE-201 - Insertion of Sensitive Information Into Sent Data
    // Validación final antes de fetch: asegurar que la URL no apunte a hosts internos o privados
    // Esta validación adicional previene que información sensible (client_id, client_secret en Basic Auth) se envíe
    // a hosts no autorizados, cumpliendo con las mejores prácticas de seguridad
    const finalValidatedUrl = this.validateUrlForFetch(oauthUrl);
    const trustedTokenUrl = ApiService.validateTrustedDestination(finalValidatedUrl, rawOauthUrl);

    const response = await fetch(trustedTokenUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${basicAuth}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      redirect: 'error',
      body: new URLSearchParams({
        grant_type: 'client_credentials',
        scope: 'TFSMPublicAPIsMediaCatalogueAPI',
      }),
    });

    if (!response.ok) {
      // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize response.status before use in error message
      // response.status es un número, pero sanitizamos por consistencia y defensa en profundidad
      const statusCode = String(response.status);
      const sanitizedStatus = this.sanitizeForLog(statusCode);
      throw new Error(`Error al obtener token: ${sanitizedStatus}`);
    }

    const data = await response.json();
    // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize access_token from API response
    // data.access_token viene de una API (fuente no confiable), sanitizar antes de asignar
    const rawAccessToken = data.access_token || '';
    this.busToken = this.sanitizeForLog(String(rawAccessToken));
    // El token expira en 1 hora (3600 segundos), renovar 5 minutos antes
    // data.expires_in es un número, validar que sea válido antes de usar
    const expiresIn = typeof data.expires_in === 'number' && data.expires_in > 0 
      ? data.expires_in 
      : 3600; // Default a 1 hora si no es válido
    this.busTokenExpiry = Date.now() + (expiresIn - 300) * 1000;

    return this.busToken;
  }

  /**
   * Valida una URL final antes de hacer fetch para prevenir SSRF
   * Veracode Flaw ID: CWE-918 - Server-Side Request Forgery (SSRF)
   * Esta validación final previene SSRF verificando que la URL no apunte a hosts internos o privados
   * @param url - URL a validar
   * @returns URL validada
   */
  private static validateUrlForFetch(url: string): string {
    if (!url || typeof url !== 'string') {
      throw new Error('URL no válida para fetch');
    }

    try {
      const urlObj = new URL(url);
      
      // Veracode Flaw ID: CWE-918 - SSRF - Validar protocolo (solo https)
      if (urlObj.protocol !== 'https:') {
        throw new Error('Solo se permiten URLs HTTPS');
      }

      // Veracode Flaw ID: CWE-918 - SSRF - Prevenir acceso a localhost/127.0.0.1
      const hostname = urlObj.hostname.toLowerCase();
      if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname === '0.0.0.0') {
        throw new Error('Acceso a localhost no permitido');
      }

      // Veracode Flaw ID: CWE-918 - SSRF - Prevenir acceso a IPs privadas
      // Validar rangos de IPs privadas: 10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16
      const ipv4Regex = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
      const ipMatch = hostname.match(ipv4Regex);
      if (ipMatch) {
        const [, a, b] = ipMatch.map(Number);
        // 10.0.0.0/8
        if (a === 10) {
          throw new Error('Acceso a IP privada no permitido');
        }
        // 172.16.0.0/12
        if (a === 172 && b >= 16 && b <= 31) {
          throw new Error('Acceso a IP privada no permitido');
        }
        // 192.168.0.0/16
        if (a === 192 && b === 168) {
          throw new Error('Acceso a IP privada no permitido');
        }
        // 127.0.0.0/8 (localhost)
        if (a === 127) {
          throw new Error('Acceso a localhost no permitido');
        }
        // 169.254.0.0/16 (link-local)
        if (a === 169 && b === 254) {
          throw new Error('Acceso a IP link-local no permitido');
        }
      }

      // Veracode Flaw ID: CWE-918 - SSRF - Validar que no sea un dominio interno
      if (hostname.endsWith('.local') || hostname.endsWith('.internal') || hostname.endsWith('.lan')) {
        throw new Error('Acceso a dominio interno no permitido');
      }

      // Veracode Flaw ID: CWE-918 - SSRF - Host debe coincidir con bases definidas en env (URL o host)
      const allowedHosts = this.getAllowedToyotaBusRelatedHostnames();
      if (allowedHosts.length === 0) {
        throw new Error('Host permitido no configurado');
      }
      if (!this.isHostnameInToyotaBusAllowList(hostname, allowedHosts)) {
        throw new Error('Host no permitido');
      }

      return urlObj.toString();
    } catch (e) {
      if (e instanceof TypeError) {
        throw new Error('URL no válida para fetch');
      }
      throw e;
    }
  }

  /**
   * Valida una URL para prevenir SSRF
   * Veracode Flaw ID: CWE-918 - Server-Side Request Forgery (SSRF)
   * Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize URL before processing
   * @param url - URL a validar (puede venir de fuente no confiable)
   * @returns URL validada y sanitizada
   */
  private static validateBusUrl(url: string): string {
    if (!url || typeof url !== 'string') {
      throw new Error('URL no válida');
    }

    // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize URL before processing
    // La URL puede venir de task.task_data (fuente no confiable), sanitizar antes de procesarla
    // para prevenir inyección CRLF en el parsing de URL
    const sanitizedUrl = this.sanitizeForLog(url);

    try {
      // Veracode Flaw ID: CWE-117 - CRLF Injection - Use sanitized URL for parsing
      // Usar la URL sanitizada para crear el objeto URL
      const urlObj = new URL(sanitizedUrl);
      
      // Solo permitir https (no http para seguridad)
      if (urlObj.protocol !== 'https:') {
        throw new Error('Solo se permiten URLs HTTPS');
      }

      // Validar que el host sea uno de los dominios permitidos (misma familia de env que OAuth BUS)
      const allowedHosts = this.getAllowedToyotaBusRelatedHostnames();
      if (allowedHosts.length === 0) {
        throw new Error('Host permitido no configurado');
      }
      const hostnameNorm = (urlObj.hostname || '').toLowerCase();
      if (!this.isHostnameInToyotaBusAllowList(hostnameNorm, allowedHosts)) {
        throw new Error('Host no permitido');
      }

      // Validar que la ruta comience con /tfsm/Cotizador/GoKey
      // Veracode Flaw ID: CWE-117 - CRLF Injection - urlObj.pathname comes from sanitized URL
      // urlObj.pathname viene de la URL sanitizada, pero sanitizamos nuevamente por defensa en profundidad
      const sanitizedPathname = this.sanitizeForLog(urlObj.pathname || '');
      if (!sanitizedPathname.startsWith('/tfsm/Cotizador/GoKey')) {
        throw new Error('Ruta no permitida');
      }

      // Veracode Flaw ID: CWE-117 - CRLF Injection - Return sanitized URL
      // Retornar la URL sanitizada para prevenir CRLF injection
      return urlObj.toString();
    } catch (e) {
      if (e instanceof TypeError) {
        throw new Error('URL no válida');
      }
      throw e;
    }
  }

  /**
   * Ejecuta una petición al BUS de Toyota
   */
  private static async makeBusRequest(url: string): Promise<{ object?: unknown[]; [key: string]: unknown }> {
    // Veracode Flaw ID: CWE-918 - SSRF - Validar URL antes de usarla
    // Veracode Flaw ID: CWE-117 - CRLF Injection - url viene de fuente no confiable, se valida y sanitiza en validateBusUrl
    const validatedUrl = this.validateBusUrl(url);
    
    // Veracode Flaw ID: CWE-918 - SSRF - Validación final antes de fetch para prevenir SSRF
    // Validar que la URL no apunte a hosts internos, IPs privadas o localhost antes de hacer fetch
    // Esta validación adicional previene SSRF incluso si validateBusUrl fue comprometido
    const finalValidatedUrl = this.validateUrlForFetch(validatedUrl);
    
    const token = await this.getBusToken();
    
    // Veracode Flaw ID: CWE-117 - CRLF Injection - token ya está sanitizado en getBusToken
    // token viene de getBusToken que ya sanitiza el access_token antes de asignarlo
    const authorizationHeader = `Bearer ${token}`;

    // Validación en sink: solo permitir dominio/ruta esperados inmediatamente antes del fetch
    const sinkSafeUrl = new URL(finalValidatedUrl);
    const busBaseUrl = process.env.VUE_APP_API_URL_TOYOTA_BUS;
    if (!busBaseUrl) {
      throw new Error('VUE_APP_API_URL_TOYOTA_BUS no está configurada');
    }

    let allowedBusOrigin = '';
    try {
      allowedBusOrigin = new URL(busBaseUrl).origin;
    } catch (_) {
      throw new Error('VUE_APP_API_URL_TOYOTA_BUS no es una URL válida');
    }

    if (sinkSafeUrl.origin !== allowedBusOrigin) {
      throw new Error('Destino BUS no permitido');
    }
    if (!sinkSafeUrl.pathname.startsWith('/tfsm/Cotizador/GoKey')) {
      throw new Error('Ruta BUS no permitida');
    }
    
    // Validación final explícita justo en el sink para facilitar trazabilidad en análisis estático.
    const strictSinkUrl = this.validateBusUrl(sinkSafeUrl.toString());
    const response = await fetch(strictSinkUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': authorizationHeader,
      },
    });

    if (!response.ok) {
      // Si es 401, el token expiró, limpiar y reintentar
      if (response.status === 401) {
        this.busToken = "";
        this.busTokenExpiry = 0;
        // Veracode Flaw ID: CWE-117 - CRLF Injection - finalValidatedUrl ya está sanitizado y validado
        // Veracode Flaw ID: CWE-918 - SSRF - finalValidatedUrl ya pasó todas las validaciones SSRF
        return this.makeBusRequest(finalValidatedUrl);
      }
      // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize response.status before use in error message
      // response.status es un número, pero sanitizamos por consistencia y defensa en profundidad
      const statusCode = String(response.status);
      const sanitizedStatus = this.sanitizeForLog(statusCode);
      throw new Error(`Error en petición al BUS: ${sanitizedStatus}`);
    }

    // Veracode Flaw ID: CWE-117 - CRLF Injection - data viene de API (fuente no confiable)
    // Los datos se sanitizarán cuando se usen en strings o se incluyan en payloads
    // (ver sanitizeResultForPayload en completeTask)
    const data = await response.json();
    return data;
  }

  /**
   * Ejecuta findBrand
   */
  private static async executeFindBrand(taskData: { brandText: string }): Promise<{ value: unknown; text: string } | null> {
    // Obtener todas las marcas
    // Veracode Flaw ID: CWE-918 - SSRF - URL hardcodeada y validada
    // Esta URL es estática y segura, pero se valida en makeBusRequest para prevenir SSRF
    const url = process.env.VUE_APP_API_URL_TOYOTA_BUS + '/tfsm/Cotizador/GoKey/Brands';
    
    const response = await this.makeBusRequest(url);
    const brands = response.object || [];

    // Buscar la marca que coincida
    // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize brandText before use in comparison
    // brandText viene de taskData (fuente no confiable), sanitizar antes de usar en comparación
    const rawBrandText = taskData.brandText || '';
    const sanitizedBrandText = this.sanitizeForLog(rawBrandText);
    
    for (const brand of brands as Array<{ value: unknown; text: string }>) {
      // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize brand.text from API before comparison and return
      // brand.text viene de API (fuente no confiable), sanitizar antes de usar y retornar
      const rawBrandTextFromApi = brand.text ? String(brand.text) : '';
      const sanitizedBrandTextFromApi = this.sanitizeForLog(rawBrandTextFromApi);
      
      if (sanitizedBrandTextFromApi && sanitizedBrandTextFromApi.toUpperCase().trim() === sanitizedBrandText.toUpperCase().trim()) {
        return {
          value: brand.value,
          text: sanitizedBrandTextFromApi, // Retornar texto sanitizado
        };
      }
    }

    return null;
  }

  /**
   * Ejecuta findModel
   */
  private static async executeFindModel(taskData: { modelText: string; brandId: number | string; type: number }): Promise<{ value: unknown; text: string } | null> {
    const { modelText, brandId, type } = taskData;
    // Veracode Flaw ID: CWE-918 - SSRF - Validar y sanitizar parámetros antes de construir URL
    const sanitizedBrandId = String(brandId).replace(/[^0-9]/g, '');
    const sanitizedType = String(type).replace(/[^0-9]/g, '');
    const url = `${process.env.VUE_APP_API_URL_TOYOTA_BUS}/tfsm/Cotizador/GoKey/carItems/${sanitizedBrandId}/${sanitizedType}`;
    
    const response = await this.makeBusRequest(url);
    const models = response.object || [];

    if (!models || !Array.isArray(models) || models.length === 0) {
      return null;
    }

    // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize modelText before use
    // modelText viene de taskData (fuente no confiable), sanitizar antes de usar
    const rawModelText = modelText || '';
    const sanitizedModelText = this.sanitizeForLog(rawModelText);
    
    // Normalizar el texto de búsqueda usando el texto sanitizado
    const normalizedSearch = this.normalizeString(sanitizedModelText);

    // Primero intentar búsqueda exacta
    for (const model of models as Array<{ value: unknown; text: string }>) {
      // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize model.text from API before comparison and return
      // model.text viene de API (fuente no confiable), sanitizar antes de usar y retornar
      const rawModelTextFromApi = model.text ? String(model.text) : '';
      const sanitizedModelTextFromApi = this.sanitizeForLog(rawModelTextFromApi);
      
      if (sanitizedModelTextFromApi && sanitizedModelTextFromApi.toUpperCase().trim() === sanitizedModelText.toUpperCase().trim()) {
        return {
          value: model.value,
          text: sanitizedModelTextFromApi, // Retornar texto sanitizado
        };
      }
    }

    // Si no hay coincidencia exacta, buscar por similitud
    let bestMatch: { value: unknown; text: string } | null = null;
    let bestSimilarity = 0;

    for (const model of models as Array<{ value: unknown; text: string }>) {
      if (!model.text) continue;

      // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize model.text from API before use
      const rawModelTextFromApi = String(model.text);
      const sanitizedModelTextFromApi = this.sanitizeForLog(rawModelTextFromApi);
      const normalizedModel = this.normalizeString(sanitizedModelTextFromApi);
      let similarity = 0;
      this.similarity(normalizedSearch, normalizedModel, (sim) => {
        similarity = sim;
      });

      if (similarity > bestSimilarity) {
        bestSimilarity = similarity;
        // Guardar el texto sanitizado junto con el modelo
        bestMatch = {
          value: model.value,
          text: sanitizedModelTextFromApi,
        };
      }
    }

    // Solo retornar si la similitud es razonable (más del 50%)
    if (bestMatch && bestSimilarity >= 50) {
      return {
        value: bestMatch.value,
        text: bestMatch.text, // Ya está sanitizado
      };
    }

    // Si no se encontró con similitud suficiente, intentar con variaciones
    // Veracode Flaw ID: CWE-117 - CRLF Injection - Variations generated from sanitized modelText
    // Las variaciones se generan del texto sanitizado, por lo que también están sanitizadas
    const variations = this.generateModelNameVariations(sanitizedModelText);
    
    for (const variation of variations) {
      // Saltar si la variación es igual al nombre original (ya lo intentamos)
      if (variation.toUpperCase().trim() === sanitizedModelText.toUpperCase().trim()) {
        continue;
      }

      const normalizedVariation = this.normalizeString(variation);
      
      // Buscar coincidencia exacta con la variación
      for (const model of models as Array<{ value: unknown; text: string }>) {
        // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize model.text from API before comparison
        const rawModelTextFromApi = model.text ? String(model.text) : '';
        const sanitizedModelTextFromApi = this.sanitizeForLog(rawModelTextFromApi);
        
        if (sanitizedModelTextFromApi && sanitizedModelTextFromApi.toUpperCase().trim() === variation.toUpperCase().trim()) {
          return {
            value: model.value,
            text: sanitizedModelTextFromApi, // Retornar texto sanitizado
          };
        }
      }

      // Buscar por similitud con la variación
      let variationBestMatch: { value: unknown; text: string } | null = null;
      let variationBestSimilarity = 0;

      for (const model of models as Array<{ value: unknown; text: string }>) {
        if (!model.text) continue;

        // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize model.text from API before use
        const rawModelTextFromApi = String(model.text);
        const sanitizedModelTextFromApi = this.sanitizeForLog(rawModelTextFromApi);
        const normalizedModel = this.normalizeString(sanitizedModelTextFromApi);
        let similarity = 0;
        this.similarity(normalizedVariation, normalizedModel, (sim) => {
          similarity = sim;
        });

        if (similarity > variationBestSimilarity) {
          variationBestSimilarity = similarity;
          // Guardar el texto sanitizado junto con el modelo
          variationBestMatch = {
            value: model.value,
            text: sanitizedModelTextFromApi,
          };
        }
      }

      // Si encontramos una buena coincidencia con la variación, retornarla
      if (variationBestMatch && variationBestSimilarity >= 50) {
        return {
          value: variationBestMatch.value,
          text: variationBestMatch.text, // Ya está sanitizado
        };
      }
    }

    return null;
  }

  /**
   * Genera variaciones del nombre del modelo para búsqueda flexible
   * Ejemplo: "Civic Coupe" -> ["Civic Coupe", "Civic"]
   */
  private static generateModelNameVariations(modelName: string): string[] {
    const variations: string[] = [];
    const trimmed = modelName.trim();
    
    if (!trimmed) {
      return variations;
    }

    // Palabras comunes que pueden estar en el nombre pero no en el BUS
    const commonWords = [
      'coupe', 'sedan', 'hatchback', 'wagon', 'suv', 'pickup', 'truck',
      'convertible', 'roadster', 'van', 'minivan', 'crossover',
      '4wd', 'awd', 'fwd', 'rwd',
      'hybrid', 'electric', 'plug-in',
      'sport', 'luxury', 'premium', 'base', 'standard',
      'door', 'doors', 'p', '5p', '4p', '3p',
      'automatic', 'manual', 'cvt',
      'turbo', 'supercharged',
      'limited', 'edition', 'special'
    ];

    // 1. Agregar el nombre original
    variations.push(trimmed);

    // 2. Extraer solo la primera palabra (ej: "Civic Coupe" -> "Civic")
    const words = trimmed.split(/\s+/);
    if (words.length > 1 && words[0] && words[0].length > 2) {
      variations.push(words[0]);
    }

    // 3. Eliminar palabras comunes del final
    let modelWithoutCommon = trimmed;
    for (const commonWord of commonWords) {
      const pattern = new RegExp(`\\s+${commonWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'i');
      modelWithoutCommon = modelWithoutCommon.replace(pattern, '');
    }
    if (modelWithoutCommon.trim() !== trimmed && modelWithoutCommon.trim()) {
      variations.push(modelWithoutCommon.trim());
    }

    // 4. Eliminar palabras comunes del principio
    let modelWithoutCommonStart = trimmed;
    for (const commonWord of commonWords) {
      const pattern = new RegExp(`^${commonWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+`, 'i');
      modelWithoutCommonStart = modelWithoutCommonStart.replace(pattern, '');
    }
    if (modelWithoutCommonStart.trim() !== trimmed && modelWithoutCommonStart.trim()) {
      variations.push(modelWithoutCommonStart.trim());
    }

    // Eliminar duplicados y retornar
    return Array.from(new Set(variations));
  }

  /**
   * Ejecuta findYear
   */
  private static async executeFindYear(taskData: { year: string | number; modelId: number | string }): Promise<Record<string, unknown> | null> {
    const { year, modelId } = taskData;
    // Veracode Flaw ID: CWE-918 - SSRF - Validar y sanitizar parámetros antes de construir URL
    const sanitizedModelId = String(modelId).replace(/[^0-9]/g, '');
    const url = `${process.env.VUE_APP_API_URL_TOYOTA_BUS}/tfsm/Cotizador/GoKey/catModel/${sanitizedModelId}`;
    
    const response = await this.makeBusRequest(url);
    const modelYears = response.object || [];

    const yearInt = parseInt(String(year));

    // Buscar el año que coincida
    for (const modelYear of modelYears as Array<Record<string, unknown>>) {
      // PRIMERO verificar que el objeto tenga idModelLorant antes de comparar
      const hasIdModelLorant = 'idModelLorant' in modelYear;
      if (!hasIdModelLorant) {
        continue; // Saltar este objeto y continuar con el siguiente
      }
      
      const idModelLorant = modelYear.idModelLorant;
      const isDelete = modelYear.isDelete;
      
      // Verificar que idModelLorant tenga un valor válido
      if (idModelLorant === null || idModelLorant === undefined || idModelLorant === '') {
        continue; // Saltar este objeto y continuar con el siguiente
      }
      
      // Log detallado de cada comparación
      const idModelLorantInt = parseInt(String(idModelLorant));
      const matches = idModelLorantInt === yearInt;
      const notDeleted = !isDelete || isDelete === false;
      
      if (matches && notDeleted) {
        // Verificar que existe la clave y que tiene un valor válido (no null, no undefined, no cadena vacía)
        const hasValidIdModelLorant = 'idModelLorant' in modelYear && 
                                      modelYear.idModelLorant !== null && 
                                      modelYear.idModelLorant !== undefined && 
                                      modelYear.idModelLorant !== '';
        
        if (!hasValidIdModelLorant) {
          return null;
        }
        
        // Retornar solo los campos necesarios según la estructura esperada (steps.json)
        // El backend espera: idModel, idCar, idModelLorant, isDelete
        // Validar que todos los campos necesarios existan antes de retornar
        if (!modelYear.idModel || !modelYear.idCar || !modelYear.idModelLorant) {
          return null;
        }
        
        const result = {
          idModel: modelYear.idModel,
          idCar: modelYear.idCar,
          idModelLorant: modelYear.idModelLorant,
          isDelete: modelYear.isDelete ?? false
        };
        
        // Validación final antes de retornar
        if (!result.idModelLorant || result.idModelLorant === null || result.idModelLorant === undefined) {
          return null;
        }
        
        return result;
      }
    }

    return null;
  }

  /**
   * Ejecuta findVersion
   */
  private static async executeFindVersion(taskData: { versionText: string; idModel: number | string; idCar: number | string }): Promise<Record<string, unknown> | null> {
    const { versionText, idModel, idCar } = taskData;
    // Veracode Flaw ID: CWE-918 - SSRF - Validar y sanitizar parámetros antes de construir URL
    const sanitizedIdModel = String(idModel).replace(/[^0-9]/g, '');
    const sanitizedIdCar = String(idCar).replace(/[^0-9]/g, '');
    const url = `${process.env.VUE_APP_API_URL_TOYOTA_BUS}/tfsm/Cotizador/GoKey/carVersion/${sanitizedIdModel}/${sanitizedIdCar}`;
    
    const response = await this.makeBusRequest(url);
    const versions = response.object || [];

    if (!versions || versions.length === 0) {
      return null;
    }

    // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize versionText before use
    // versionText viene de taskData (fuente no confiable), sanitizar antes de usar
    const rawVersionText = versionText || '';
    const sanitizedVersionText = this.sanitizeForLog(rawVersionText);
    const normalizedSearch = this.normalizeString(sanitizedVersionText);
    let bestMatch: Record<string, unknown> | null = null;
    let bestSimilarity = 0;

    // Primero intentar búsqueda exacta (case insensitive)
    for (const version of versions as Array<Record<string, unknown>>) {
      // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize keyVersion and nameVersion from API
      // keyVersion y nameVersion vienen de API (fuente no confiable), sanitizar antes de usar
      const rawKeyVersion = (version.keyVersion as string) || '';
      const rawNameVersion = (version.nameVersion as string) || '';
      const sanitizedKeyVersion = this.sanitizeForLog(rawKeyVersion);
      const sanitizedNameVersion = this.sanitizeForLog(rawNameVersion);
      const versionToCompare = sanitizedKeyVersion || sanitizedNameVersion;

      if (!versionToCompare) continue;

      // Búsqueda exacta (case insensitive) usando valores sanitizados
      if (versionToCompare.toUpperCase().trim() === sanitizedVersionText.toUpperCase().trim()) {
        // Veracode Flaw ID: CWE-117 - CRLF Injection - Return sanitized version object
        // Retornar versión con campos de texto sanitizados
        return {
          ...version,
          keyVersion: sanitizedKeyVersion || version.keyVersion,
          nameVersion: sanitizedNameVersion || version.nameVersion,
        };
      }
    }

    // Si no hay coincidencia exacta, buscar por similitud
    for (const version of versions as Array<Record<string, unknown>>) {
      // Veracode Flaw ID: CWE-117 - CRLF Injection - Sanitize keyVersion and nameVersion from API
      const rawKeyVersion = (version.keyVersion as string) || '';
      const rawNameVersion = (version.nameVersion as string) || '';
      const sanitizedKeyVersion = this.sanitizeForLog(rawKeyVersion);
      const sanitizedNameVersion = this.sanitizeForLog(rawNameVersion);
      const versionToCompare = sanitizedKeyVersion || sanitizedNameVersion;

      if (!versionToCompare) continue;

      const normalizedVersion = this.normalizeString(versionToCompare);
      let similarity = 0;
      this.similarity(normalizedSearch, normalizedVersion, (sim) => {
        similarity = sim;
      });

      // También verificar si alguna palabra clave del texto de búsqueda está en la versión
      const searchWords = normalizedSearch.split(' ').filter(w => w.length > 2);
      const versionWords = normalizedVersion.split(' ').filter(w => w.length > 2);
      const commonWords = searchWords.filter(w => versionWords.includes(w));
      const wordMatchBonus = (commonWords.length / Math.max(searchWords.length, 1)) * 20; // Bonus hasta 20%
      const finalSimilarity = similarity + wordMatchBonus;

      if (finalSimilarity > bestSimilarity) {
        bestSimilarity = finalSimilarity;
        // Guardar versión con campos sanitizados
        bestMatch = {
          ...version,
          keyVersion: sanitizedKeyVersion || version.keyVersion,
          nameVersion: sanitizedNameVersion || version.nameVersion,
        };
      }
    }

    // Reducir el umbral de similitud a 20% para ser más flexible
    // Pero también considerar si hay palabras comunes
    if (bestMatch && bestSimilarity >= 20) {
      // bestMatch ya tiene campos sanitizados
      return bestMatch;
    }

    return null;
  }

  /**
   * Ejecuta una petición GET genérica al BUS de Toyota
   */
  private static async executeBusRequest(taskData: { url: string }): Promise<unknown> {
    // Hacer la petición GET al BUS usando el método existente
    const response = await this.makeBusRequest(taskData.url);
    // Retornar el objeto 'object' de la respuesta
    return response.object ?? response;
  }

  /**
   * Normaliza un string para comparación
   */
  private static normalizeString(str: string): string {
    return str
      .toUpperCase()
      .replace(/\s+/g, ' ')
      .replace(/[/\-_.]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Calcula similitud entre dos strings (similar a similar_text de PHP)
   */
  private static similarity(str1: string, str2: string, callback: (similarity: number) => void): void {
    // Implementación simplificada de similitud
    // Para una mejor implementación, se podría usar una librería como string-similarity
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) {
      callback(100);
      return;
    }

    // Calcular distancia de Levenshtein
    const distance = this.levenshteinDistance(longer, shorter);
    const similarity = ((longer.length - distance) / longer.length) * 100;

    callback(similarity);
  }

  /**
   * Calcula la distancia de Levenshtein entre dos strings
   */
  private static levenshteinDistance(str1: string, str2: string): number {
    const matrix: number[][] = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }
}

export default BusProxyService;

